import { useEffect } from "react";

interface UseDelayedCallbackOptions {
  callback: () => void;
  delay: number;
  shouldExecute: boolean;
}

/**
 * Executes a callback after a specified delay when a condition is met.
 * Uses requestAnimationFrame and setTimeout for smooth execution timing.
 */
const useDelayedCallback = ({
  callback,
  delay,
  shouldExecute,
}: UseDelayedCallbackOptions): void => {
  useEffect(() => {
    if (!shouldExecute) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const raf = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }

      timeoutId = setTimeout(() => {
        if (!cancelled) {
          callback();
        }
      }, delay);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);

      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [callback, delay, shouldExecute]);
};

export default useDelayedCallback;
